import { domainOf } from '../../lib/url';
import type { FeedSourceRead, NewsItemRead } from '../../types/api';

/**
 * Reading the list down, and putting it in an order.
 *
 * Everything here is a pure function over items the client already holds.
 * Narrowing the screen is not a reason to go back to the network — and going
 * back would cost a re-pull of somebody else's website, not a cheap query.
 *
 * Nothing in this module touches React, storage or the API, so the whole of the
 * filter's meaning can be exercised on plain arrays.
 */

/** Which way `published_at` runs. Undated items are last either way. */
export type NewsSortOrder = 'newest' | 'oldest';

/**
 * Everything the filter panel decides, as one value.
 *
 * Each field narrows and nothing widens, so an empty filter is the whole feed
 * and `activeFilterCount` is exactly what the Filter button badges.
 */
export interface NewsFilter {
  /** Only items the server tagged `industry` — the old "My industry" chip. */
  industryOnly: boolean;
  /** Only items from a source filed under *some* category. */
  anyCategory: boolean;
  /** Category ids to keep. Empty means the categories dimension asks nothing. */
  categoryIds: readonly string[];
  /** Source ids to keep. Empty means every source. */
  sourceIds: readonly string[];
}

/** The whole feed: no dimension asks anything. */
export const EMPTY_NEWS_FILTER: NewsFilter = {
  industryOnly: false,
  anyCategory: false,
  categoryIds: [],
  sourceIds: [],
};

/** The order the toolbar opens in. */
export const DEFAULT_NEWS_SORT: NewsSortOrder = 'newest';

/** One provider in the panel's list: a source id, how to name it, where it is filed. */
export interface NewsSourceOption {
  id: string;
  label: string;
  /** Bare domain, for the second line. Null when the URL yields none. */
  domain: string | null;
  /** The category this source is filed under, or null for the loose bucket. */
  categoryId: string | null;
}

/** Name of a source: its own title, else its domain, else the raw URL. */
function sourceOptionLabel(source: FeedSourceRead): string {
  return source.title?.trim() || domainOf(source.site_url ?? source.feed_url) || source.feed_url;
}

/**
 * The provider list.
 *
 * The business's own sources, in the order the collection hands them over, plus
 * any source id that appears on an item but not in that list — a row removed
 * since the cached pull, say. Without the second pass those items would sit in
 * the list under a provider nothing could tick.
 */
export function newsSourceOptions(
  sources: readonly FeedSourceRead[],
  items: readonly NewsItemRead[],
): NewsSourceOption[] {
  const options: NewsSourceOption[] = sources.map((source) => ({
    id: source.id,
    label: sourceOptionLabel(source),
    domain: domainOf(source.site_url ?? source.feed_url),
    categoryId: source.category_id,
  }));
  const known = new Set(options.map((option) => option.id));

  for (const item of items) {
    if (!item.source_id || known.has(item.source_id)) continue;
    known.add(item.source_id);
    options.push({
      id: item.source_id,
      label: item.source.trim() || domainOf(item.url) || 'Unknown source',
      domain: domainOf(item.url),
      categoryId: null,
    });
  }

  return options;
}

/**
 * Where each source is filed, keyed by the id an item carries.
 *
 * The item says which source it came out of; only the source row knows which
 * folder that is. This map is the join, built once per render of the list.
 */
export function sourceCategoryIndex(
  options: readonly NewsSourceOption[],
): ReadonlyMap<string, string | null> {
  return new Map(options.map((option) => [option.id, option.categoryId]));
}

/**
 * Drops selections that no longer name anything — a source that was unfollowed,
 * a category that was deleted, or "in a category" when there are no categories
 * left at all.
 *
 * Held loosely on purpose: a stale saved default should quietly stop asking for
 * what has gone rather than empty the screen with no way to see why.
 */
export function pruneNewsFilter(
  filter: NewsFilter,
  knownSourceIds: ReadonlySet<string>,
  knownCategoryIds: ReadonlySet<string>,
): NewsFilter {
  const sourceIds = filter.sourceIds.filter((id) => knownSourceIds.has(id));
  const categoryIds = filter.categoryIds.filter((id) => knownCategoryIds.has(id));
  const anyCategory = filter.anyCategory && knownCategoryIds.size > 0;

  if (
    sourceIds.length === filter.sourceIds.length &&
    categoryIds.length === filter.categoryIds.length &&
    anyCategory === filter.anyCategory
  ) {
    return filter;
  }
  return { ...filter, anyCategory, categoryIds, sourceIds };
}

/** How many separate answers the filter is holding. Zero is "the whole feed". */
export function activeFilterCount(filter: NewsFilter): number {
  return (
    (filter.industryOnly ? 1 : 0) +
    (filter.anyCategory ? 1 : 0) +
    filter.categoryIds.length +
    filter.sourceIds.length
  );
}

export function isFilterActive(filter: NewsFilter): boolean {
  return activeFilterCount(filter) > 0;
}

/** Same set of answers? Order inside a selection is not part of the question. */
function sameIds(left: readonly string[], right: readonly string[]): boolean {
  if (left.length !== right.length) return false;
  const known = new Set(right);
  return left.every((id) => known.has(id));
}

/** Whether two filters ask the same thing — what "your default is applied" means. */
export function isSameFilter(left: NewsFilter, right: NewsFilter): boolean {
  return (
    left.industryOnly === right.industryOnly &&
    left.anyCategory === right.anyCategory &&
    sameIds(left.categoryIds, right.categoryIds) &&
    sameIds(left.sourceIds, right.sourceIds)
  );
}

/** Does the item's source sit in a folder the categories dimension asked for? */
function matchesCategory(
  item: NewsItemRead,
  filter: NewsFilter,
  categoryBySource: ReadonlyMap<string, string | null>,
): boolean {
  if (!filter.anyCategory && filter.categoryIds.length === 0) return true;
  const categoryId = categoryBySource.get(item.source_id) ?? null;
  if (categoryId === null) return false;
  // "In a category" is satisfied by any folder; a named folder is satisfied by
  // itself. The two read as one question, so they are answered with an or.
  return filter.anyCategory || filter.categoryIds.includes(categoryId);
}

/**
 * The visible list: every dimension narrows, so an item has to pass all of them.
 *
 * `categoryBySource` is only consulted when the categories dimension asks
 * something, so a caller with no categories can pass an empty map.
 */
export function filterNewsItems(
  items: readonly NewsItemRead[],
  filter: NewsFilter,
  categoryBySource: ReadonlyMap<string, string | null>,
): NewsItemRead[] {
  return items.filter(
    (item) =>
      (!filter.industryOnly || item.tags.includes('industry')) &&
      matchesCategory(item, filter, categoryBySource) &&
      (filter.sourceIds.length === 0 || filter.sourceIds.includes(item.source_id)),
  );
}

/** Milliseconds for an item's date, or null when it has none we can read. */
function publishedAt(item: NewsItemRead): number | null {
  if (!item.published_at) return null;
  const parsed = Date.parse(item.published_at);
  return Number.isNaN(parsed) ? null : parsed;
}

/**
 * The list in date order.
 *
 * An item with no usable `published_at` is last in both directions: "oldest
 * first" is a question about dates, and an undated piece is not the answer to
 * it. Ties and undated items keep the order the feed handed them over in —
 * `Array.prototype.sort` is stable, and the comparator returns 0 for them.
 */
export function sortNewsItems(
  items: readonly NewsItemRead[],
  order: NewsSortOrder,
): NewsItemRead[] {
  return [...items].sort((left, right) => {
    const a = publishedAt(left);
    const b = publishedAt(right);
    if (a === null || b === null) {
      if (a === b) return 0;
      return a === null ? 1 : -1;
    }
    return order === 'newest' ? b - a : a - b;
  });
}
